import OAuthClient from "@/helpers/oauth";
import axios from "axios";

import cookie from "js-cookie"
import api from '../../../helpers/api'
import { get, map, random, includes, find } from 'lodash'
import {
    getAccountDomain,
    getUserRegistrationPage,
} from '../../../constants/urls'
import Page from '../pages/model'
import UserRole from '../user-roles/model'

import CryptoJS from 'crypto-js'
import { span } from '../../../helpers/tracer'
import { ADMIN, ENGINEER, STUDENT } from '../roles/model'

const createRandomString = (num) => {
    return [...Array(num)].map(() => Math.random().toString(36)[2]).join('')
}

const getUser = () => {
    const p = cookie.get('csu')
    const str = cookie.get('cu')
    try {
        return str ? JSON.parse(CryptoJS.AES.decrypt(str, p).toString(CryptoJS.enc.Utf8)) : null
    } catch (e) {
        return null
    }
}

const setUser = (user) => {
    const salt = createRandomString(32)
    cookie.set('csu', salt)
    cookie.set('cu', CryptoJS.AES.encrypt(JSON.stringify(user), salt).toString())
}

const translateAuthUser = (authUser) => {
    return {
        id: get(authUser, 'id'),
        firstName: get(authUser, 'name'),
        lastName: get(authUser, 'last_name'),
        surName: get(authUser, 'surname'),
        email: get(authUser, 'email'),
        profile_photo_path: get(authUser, 'profile_photo_path'),
    }
}

const resetUser = (store) => {
    cookie.remove('csu')
    cookie.remove('cu')
    store.commit('setCurrentUserId', null)
}

const LIMIT_FAILED_AUTH = 5;
const MIN_REFRESH_SEC = 120;
const MAX_REFRESH_SEC = 140;
let currentFailedAuth = 0;

const handleToken = (store, token) => {
    return new Promise((resolve, reject) => {
        if (token && !token.isExpired) {
            store.commit('setToken', token)
            const time = (token.expiresIn * 1000) - (random(MIN_REFRESH_SEC, MAX_REFRESH_SEC) * 1000)
            const timeout = setTimeout(
                () => store.dispatch('refreshToken'),
                time > 0 ? time : (currentFailedAuth === 0 ? 1000 : currentFailedAuth * 500)
            )
            store.commit('setRefreshTimeout', timeout)

            store.dispatch('insertOrUpdate', { data: translateAuthUser(token.user) })
            setUser(translateAuthUser(token.user))

            resolve();
        } else {
            store.commit('clearToken')
            const user = getUser()
            if(user !== null) {
                store.dispatch('insertOrUpdate', { data: user })
                store.commit('setCurrentUserId', user.id)
            }

            reject()
        }
    })
}

export default {
    state: {
        refreshTimeout: null,
        expiredTimeout: null,
        currentUserId: null,
        tokenExpired: true,
        userInitialized: false,
        currentUserRoleId: null,
        currentPositionId: null,
        currentOrganizationId: null,
    },

    mutations: {
        setToken (state, token) {
            if (state.expiredTimeout) clearTimeout(state.expiredTimeout);
            state.expiredTimeout = setTimeout(() => {
                state.tokenExpired = true
            }, token.expiresIn * 1000)
            state.tokenExpired = !token.expiresIn > 0
            state.currentUserId = token.user.id
        },
        clearToken(state) {
            if (state.expiredTimeout) clearTimeout(state.expiredTimeout);
            if (state.refreshTimeout) clearTimeout(state.refreshTimeout);
            state.tokenExpired = true
        },
        setRefreshTimeout(state, refreshTimeout) {
            if (state.refreshTimeout) clearTimeout(state.refreshTimeout);
            state.refreshTimeout = refreshTimeout
        },
        setCurrentUserId(state, userId) {
            state.currentUserId = userId
        },

    },

    getters: {
        isLoggedIn (state, getters) {
            return OAuthClient.getToken() && !state.tokenExpired
        },
        currentUser (state, getters) {
            return state.currentUserId ? getters.query().with(['position', 'userRolePivot.role', 'userRolePivot.group', 'userRolePivot.position', 'userRolePivot.organization']).find(state.currentUserId) : null
        },
        currentUserId (state, getters) {
            return state.currentUserId
        },
        currentUserRoleType(state, getters) {
            return get(find(get(getters.currentUser, 'currentRoles', []), item => item.roleId === getters.currentUserRoleId), 'role.sysname', STUDENT)
        },
        currentUserRoleIsEngineer(state, getters) {
            return getters.currentUserRoleType === ENGINEER
        },
        currentUserRoleIsAdmin(state, getters) {
            return getters.currentUserRoleType === ADMIN
        },
        currentUserRoleId(state, getters) {
            let userRoleId = null
            if (state.currentUserRoleId !== null) {
                userRoleId = state.currentUserRoleId
            }

            if (!userRoleId) {
                userRoleId = cookie.get('currentUserRoleId')
            }
            if (userRoleId) {
                if (!includes(map(get(getters.currentUser, 'currentRoles'), 'role.id'), userRoleId)) {
                    userRoleId = null
                }
            }

            if (!userRoleId) {
                userRoleId = get(getters.currentUser, 'currentRoles.0.role.id', null)
            }

            if (userRoleId) {
                cookie.set('currentUserRoleId', userRoleId)
            }
            return userRoleId
        },
        currentOrganizationId(state, getters) {
            let organizationId = null
            if (state.currentOrganizationId !== null) {
                organizationId = state.currentOrganizationId
            }

            if (!organizationId) {
                organizationId = cookie.get('currentOrganizationId')
            }
            if (organizationId) {
                if (!includes(map(get(getters.currentUser, 'currentRoles'), 'organization.id'), organizationId)) {
                    organizationId = null
                }
            }

            if (!organizationId) {
                organizationId = get(getters.currentUser, 'currentRoles.0.organization.id', null)
            }

            if (organizationId) {
                cookie.set('currentOrganizationId', organizationId)
            }

            return organizationId
        },
        currentPositionId(state, getters) {
            let positionId = null
            if (state.currentPositionId !== null) {
                positionId = state.currentPositionId
            }

            if (!positionId) {
                positionId = cookie.get('currentPositionId')
            }
            if (positionId) {
                if (!includes(map(get(getters.currentUser, 'currentRoles'), 'position.id'), positionId)) {
                    positionId = null
                }
            }

            if (!positionId) {
                positionId = get(getters.currentUser, 'currentRoles.0.position.id', null)
            }

            if (positionId) {
                cookie.set('currentPositionId', positionId)
            }
            return positionId
        },
        registrationPage() {
            return getUserRegistrationPage()
        },
        userById(state, getters) {
            return id => getters.query().with(['organization.parent.parent.parent', 'position']).find(id) || null
        },
        userInitialized (state, getters) {
            return state.userInitialized
        },
    },
    actions: {
        fetchCurrent(store) {
            Page.dispatch('addLoader', 'users.fetchById')

            return api.get(`/api/v1/users/GetByToken`)
            .then(res => {
                let user = res.data.data

                setUser(user)
                store.dispatch('insertOrUpdate', { data: user })
                store.dispatch('setUserInitialized')

                return res.data
            }).finally(()=>{
                Page.dispatch('removeLoader', 'users.fetchById')
            })
        },
        fetchItems(store, params) {
            Page.dispatch('addLoader', 'users.fetchItems')

            return api.get(`/api/v1/users`, {params})
            .then(res => {
                let users = res.data.data

                store.dispatch('insertOrUpdate', { data: users })


                return res.data
            }).finally(()=>{
                Page.dispatch('removeLoader', 'users.fetchItems')
            })
        },
        fetchById(store, userId) {
            Page.dispatch('addLoader', 'users.fetchById')

            return api.get(`/api/v1/users`, {params: {Id: userId}})
            .then(res => {
                let users = res.data.data
                UserRole.dispatch('removeRolesByUser', userId)
                store.dispatch('insertOrUpdate', { data: users })


                return res.data
            }).finally(()=>{
                Page.dispatch('removeLoader', 'users.fetchById')
            })
        },
        fetchUsersStats(store, params) {
            Page.dispatch('addLoader', 'users.fetchUsersStats')

            return api.get(`/api/v1/users/ActivityStatistic`, {params})
            .then(res => {
                let users = map(res.data.data, 'user')
                store.dispatch('insertOrUpdate', { data: users })


                return res.data
            }).finally(()=>{
                Page.dispatch('removeLoader', 'users.fetchUsersStats')
            })
        },
        update(store, user) {
            Page.dispatch('addLoader', 'users.update')

            return api.post(`/api/v1/users/Update`, user)
            .then(res => {
                store.dispatch('insertOrUpdate', { data: res.data.data })
                return res.data
            }).finally(()=>{
                Page.dispatch('removeLoader', 'users.update')
            })
        },
        save (store, data) {
            Page.dispatch('addLoader', 'users.save')
            return store.dispatch(data.id ? 'update' : 'create', data).finally(()=>{
                Page.dispatch('removeLoader', 'users.save')
            })
        },
        refreshToken(store) {
            return new Promise((resolve, reject) => {
                if (currentFailedAuth > LIMIT_FAILED_AUTH) {
                    store.dispatch('forceLogoutFromApp')
                    reject()
                    return
                }

                OAuthClient.renewToken().then((token) => {
                    currentFailedAuth = 0
                    handleToken(store, token).then(resolve).catch(reject)
                }).catch((e) => {
                    const token = OAuthClient.getToken()
                    if (!token || token.expiresIn < MIN_REFRESH_SEC) {
                        currentFailedAuth++
                    }
                    handleToken(store, token).then(resolve).catch(reject)
                    if (currentFailedAuth === 1) throw e
                })
            })
        },
        handleToken(store, token) {
            return handleToken(store, token)
        },
        restoreToken (store) {
            return new Promise((resolve, reject) => {
                const token = OAuthClient.getToken()
                handleToken(store, token).then(resolve).catch(reject)
            })
        },
        login() {

        },
        forceLogoutFromApp(store) {
            OAuthClient.saveToken(null)
            return handleToken(store, null).then(()=>{
                window.location.href = '/login'
            }).catch((e)=>{
                window.location.href = '/login'
            })
        },
        logout(store, silent = false) {
            store.state.userInitialized = false
            return new Promise((resolve, reject) => {
                axios.get(getAccountDomain() + '/sanctum/csrf-cookie', {
                    headers: {
                        'request-id': span._generateUUID(),
                        'span-id': span.uuid(),
                        'trace-id': span._generateUUID(),
                    }
                }).then(()=>{
                    if (!silent) resetUser(store)

                    axios.post(getAccountDomain() + '/logout', null, {
                        withCredentials: true,
                        headers: {
                            'request-id': span._generateUUID(),
                            'span-id': span.uuid(),
                            'trace-id': span._generateUUID(),
                        }
                    }).catch(()=>{})

                    OAuthClient.logout().then(() =>{

                        handleToken(store, null).then(resolve).catch(resolve)

                    })
                })
            })
        },
        setUserInitialized(store, isInit = true) {
            store.state.userInitialized = isInit
        },
        setCurrentUserRoleId(store, id) {
            cookie.remove('currentUserRoleId')
            cookie.set('currentUserRoleId', id)
            store.state.currentUserRoleId = id
        },
        setCurrentPositionId(store, id) {
            cookie.remove('currentPositionId')
            cookie.set('currentPositionId', id)
            store.state.currentPositionId = id
        },
        setCurrentOrganizationId(store, id) {
            cookie.remove('currentOrganizationId')
            cookie.set('currentOrganizationId', id)
            store.state.currentOrganizationId = id
        }
    }
}
